import { useQuery } from '@tanstack/react-query'
import type { EmailSend, EmailCampaign } from '@/server/modules/email/db/schema'
import type { Contact } from '@/server/modules/contacts/db/schema'
import { buildQueryString, fetchJson } from '@/client/lib/api-helpers'

/**
 * TanStack Query hooks for email sends
 *
 * Provides data fetching for email send logs and statistics
 */

// Base URL for API requests
const SENDS_API = '/api/email/sends'

// Query keys for cache management
export const SENDS_KEYS = {
  all: ['email-sends'] as const,
  lists: () => [...SENDS_KEYS.all, 'list'] as const,
  list: (params?: SendsQuery) => [...SENDS_KEYS.lists(), params] as const,
  details: () => [...SENDS_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...SENDS_KEYS.details(), id] as const,
  campaign: (campaignId: string) => [...SENDS_KEYS.all, 'campaign', campaignId] as const,
  stats: () => [...SENDS_KEYS.all, 'stats'] as const,
}

// Query parameters for email sends
export interface SendsQuery {
  campaignId?: string
  notificationType?: string
  status?: 'pending' | 'sent' | 'failed' | 'bounced'
  contactId?: string
  limit?: number
  offset?: number
}

// Extended send with related data
export interface EmailSendWithRelations extends EmailSend {
  contact?: Pick<Contact, 'id' | 'firstName' | 'lastName' | 'email'> | null
  campaign?: Pick<EmailCampaign, 'id' | 'name' | 'subject'> | null
}

// API response types
interface SendsResponse {
  sends: EmailSendWithRelations[]
  count: number
  total: number
}

interface CampaignSendsResponse {
  campaign: EmailCampaign
  sends: EmailSendWithRelations[]
  stats: {
    total: number
    sent: number
    failed: number
    pending: number
    bounced: number
  }
}

interface StatsResponse {
  stats: {
    total: number
    sent: number
    failed: number
    pending: number
    bounced: number
  }
  recentSends: Array<{
    id: string
    recipientEmail: string
    recipientName: string | null
    subject: string
    status: string
    createdAt: string | Date
  }>
}

/**
 * Fetch email sends with optional filters
 */
export function useEmailSends(params?: SendsQuery) {
  return useQuery({
    queryKey: SENDS_KEYS.list(params),
    queryFn: async (): Promise<SendsResponse> => {
      return fetchJson<SendsResponse>(`${SENDS_API}${buildQueryString(params)}`)
    },
  })
}

/**
 * Fetch a single email send by ID
 */
export function useEmailSend(id: string) {
  return useQuery({
    queryKey: SENDS_KEYS.detail(id),
    queryFn: async (): Promise<EmailSendWithRelations> => {
      const data = await fetchJson<{ send: EmailSendWithRelations }>(`${SENDS_API}/${id}`)
      return data.send
    },
    enabled: !!id,
  })
}

/**
 * Fetch sends for a specific campaign with stats
 */
export function useCampaignSends(campaignId: string) {
  return useQuery({
    queryKey: SENDS_KEYS.campaign(campaignId),
    queryFn: async (): Promise<CampaignSendsResponse> => {
      return fetchJson<CampaignSendsResponse>(`${SENDS_API}/campaign/${campaignId}`)
    },
    enabled: !!campaignId,
  })
}

/**
 * Fetch email sends summary statistics
 */
export function useEmailSendsStats() {
  return useQuery({
    queryKey: SENDS_KEYS.stats(),
    queryFn: async (): Promise<StatsResponse> => {
      return fetchJson<StatsResponse>(`${SENDS_API}/stats/summary`)
    },
  })
}
